const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/selectors/patientsSelectors.ts';
import Imm from 'immutable';
import { createSelector } from 'reselect';
import { CommonStoreState } from '../reducers';
import { Patient } from '../defs/kisim';
import { loadPatientPicture } from '@cistec/common_web/actions/patientsActions';
import { useRemoteData2 } from '@cistec/common_web/utils/redux4Requests';
import { emptyMap } from '@cistec/common_web/utils/generalUtils';

export interface IPatKeyData {
    patnr?: string;
    firstname?: string;
    lastname?: string;
    birthday?: string;
}

// simple selectors
export const isLoading = (state: CommonStoreState): boolean => Boolean(state.patients.get('loading'));
export const selectPatientsImm = (state: CommonStoreState): Imm.Map<string, any> => state.patients.get('patients');
export const images = (state: CommonStoreState): Imm.Map<string, any> => state.patients.get('images');
export const currentPatientFromPatient = (state: CommonStoreState): string =>
    state.patients.get('currentPatient') || '';
export const currentPatientFromLogin = (state: CommonStoreState): string => state.login?.get('patnr') || '';
export const currentPatientFromEmailLogin = (state: CommonStoreState): Imm.Map<string, Imm.Set<string>> =>
    state.login?.get('userDetails');
const selectStammdatenRoot = (state: CommonStoreState): Imm.Map<string, any> =>
    state.patients.get('stammdaten') ?? emptyMap;

export const selectCurrentPatientNr = createSelector(
    [currentPatientFromPatient, currentPatientFromLogin],
    (currentPatientFromPatient, currentPatientFromLogin) => {
        if (currentPatientFromLogin) {
            return currentPatientFromLogin;
        } else {
            return currentPatientFromPatient;
        }
    }
);

export const getIsLoggedInByEmailWithoutPatnr = createSelector(
    [currentPatientFromEmailLogin, currentPatientFromLogin],
    (emailLogin, currentPatientFromLogin) => {
        if (emailLogin && !currentPatientFromLogin) {
            return true;
        }
        return false;
    }
);

export const selectCurrentPatData = createSelector(
    [selectPatientsImm, selectCurrentPatientNr],
    (patients: Imm.Map<string, any>, patnr: string): Imm.Map<string, any> => {
        return patients?.get(patnr) || emptyMap;
    }
);

export const selectCurrentPatStammdaten = createSelector(
    [selectStammdatenRoot, selectCurrentPatientNr],
    (stammdaten: Imm.Map<string, any>, patnr: string): Imm.Map<string, any> => {
        return stammdaten?.get(patnr) || emptyMap;
    }
);

export const makeSelectPatient = (patnr: string) =>
    createSelector([selectPatientsImm], (_patients: Imm.Map<string, any>): Patient | undefined => {
        if (_patients) {
            return _patients.get(patnr)?.toJS() || undefined;
        }
        return undefined;
    });

export const currentPatKeyData = createSelector(
    [selectCurrentPatData],
    (currentPatData: Imm.Map<string, any>): IPatKeyData => {
        if (currentPatData.isEmpty()) return {};

        return {
            patnr: currentPatData.get('PATNR'),
            firstname: currentPatData.get('VORNAME'),
            lastname: currentPatData.get('NAME'),
            birthday: currentPatData.get('GEBDAT'),
        };
    }
);

/**
 * @return One of:
 *    undefined, which indicates the patient picture has not been loaded yet;
 *    'failed', which indicates we tried to load the patient picture, but failed;
 *    otherwise the base64 encoded image
 */
export const currentPatPic = createSelector(
    [images, selectCurrentPatientNr],
    (images: Imm.Map<string, any>, currentPatient: string): string | undefined =>
        images?.getIn([currentPatient, 'data']) || undefined
);

export const currentPatPicUploading = createSelector(
    [images, selectCurrentPatientNr],
    (images: Imm.Map<string, any>, currentPatient: string): boolean =>
        images?.getIn([currentPatient, 'uploading']) || false
);

const makeSelectPatientPicture = (patnr: string) =>
    createSelector(
        [images],
        (images: Imm.Map<string, any>): string | undefined => images?.getIn([patnr, 'data']) || undefined
    );

export function usePatientPicture(patnr: string) {
    return useRemoteData2('patients', {
        canRequest: !!patnr,
        request: dispatch => dispatch(loadPatientPicture(patnr)),
        keys: ['avatars', patnr],
        select: makeSelectPatientPicture(patnr),
    });
}
